import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BarsTransition } from '@tanium/react-loading-transition';
import { FormSelectInput } from '@tanium/react-form-select-input';
import { FormSection, FormRow } from '@tanium/react-form';
import get from 'lodash.get';
import { Field, FieldProps, FormikErrors, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useQueryParam, StringParam } from 'use-query-params';
import { useTheme } from '@tanium/react-theme-context';
import { Stack } from '@tanium/react-stack';
import { Box } from '@tanium/react-box';
import GlobalServiceSelectors from 'components/pages/console/store/GlobalServiceSelectors';
import GlobalServiceSlice from 'components/pages/console/store/GlobalServiceSlice';
import IDropdownOption from 'common/IDropdownOption';
import {
  TARGET_ID,
  TARGET_NAME,
} from 'components/pages/request/requestPartials/createRequestFieldsPartials/Environments';
import { ACCESS_TYPE } from 'components/pages/request/requestPartials/createRequestFieldsPartials/AccessType';
import { ACCESS_TYPES } from 'components/pages/request/CreateRequestPage';
import { isProcessing } from 'store/ActionState';
import Tooltip from 'components/tooltip/Tooltip';

export const GlobalServiceEnvironment = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const formik = useFormikContext();
  const theme = useTheme();
  const globalServices = useSelector(GlobalServiceSelectors.selectAll);
  const selectFetchServicesState = useSelector(GlobalServiceSelectors.selectFetchServicesState);
  const [globalServiceOptions, setGlobalServiceOptions] = useState<IDropdownOption[]>([]);
  const [targetIdQuery, setTargetIdQuery] = useQueryParam(TARGET_ID, StringParam);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setAccessTypeQuery] = useQueryParam(ACCESS_TYPE, StringParam);

  useEffect(() => {
    dispatch(GlobalServiceSlice.actions.fetchAllServices({}));
  }, [dispatch]);

  useEffect(() => {
    if (globalServices && globalServices.length) {
      setGlobalServiceOptions(globalServices.map((s) => ({ label: s.name, value: s.id, description: s.description })));
    }
  }, [setGlobalServiceOptions, globalServices]);

  // Ran on initialization (with making sure GlobalServices have a non-empty array)
  useEffect(() => {
    if (targetIdQuery && globalServices && globalServices.length) {
      void formik.setFieldValue(TARGET_ID, targetIdQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalServices]);

  const formInputIdPrefix = 'create-request-';
  return (
    <FormSection title={t('SELECT_SERVICE')}>
      <Field name={TARGET_ID} data-testid={TARGET_ID}>
        {({ field, form }: FieldProps): React.ReactElement => (
          <FormRow
            label={t('GLOBAL_SERVICE')}
            required
            description={t('GLOBAL_SERVICE_INFO')}
            errors={(get(form.touched, field.name) && get(form.errors, field.name)) as FormikErrors<string>}
            labelId={`${formInputIdPrefix}${field.name}`}
          >
            {<BarsTransition isLoading={isProcessing(selectFetchServicesState)} />}
            {!isProcessing(selectFetchServicesState) && globalServices && (
              <FormSelectInput
                placeholder={t('GLOBAL_SERVICE_PLACEHOLDER')}
                onChange={(option): void => {
                  void formik.setFieldValue(TARGET_ID, option.value);
                  void formik.setFieldValue(TARGET_NAME, option.label);
                  void formik.setFieldValue(ACCESS_TYPE, '');
                  void formik.setFieldValue(ACCESS_TYPES, []);
                  setTargetIdQuery(option.value);
                  setAccessTypeQuery(undefined);
                }}
                value={globalServiceOptions.find((option) => {
                  return option.value === field.value;
                })}
                inputId={`${formInputIdPrefix}${field.name}`}
                options={globalServiceOptions}
                aria-label={t('GLOBAL_SERVICE')}
                formatOptionLabel={(
                  option: {
                    value: string;
                    label: string;
                    description: string;
                  },
                  state
                ) => {
                  const isSelected =
                    state.context === 'menu' && Array.isArray(state.selectValue) && option === state.selectValue[0];
                  /* istanbul ignore next */
                  const subtextColor = theme.colors[isSelected ? 'subdued' : 'subtle'];
                  return (
                    <Stack direction="column" itemSpacing={0.5}>
                      <Box>{option.label}</Box>
                      <Tooltip content={option.description}>
                        <Box color={subtextColor} fontSize={11}>
                          {option.description}
                        </Box>
                      </Tooltip>
                    </Stack>
                  );
                }}
                styles={{
                  singleValue: (base) => ({
                    ...base,
                    marginTop: theme.space[1],
                    marginBottom: theme.space[1],
                    position: 'relative',
                    transform: 'none',
                    whiteSpace: 'normal',
                    maxHeight: '3.75em',
                  }),
                  input: (base) => ({
                    ...base,
                    position: 'absolute',
                    top: theme.space[1] - 2,
                    paddingTop: 0,
                  }),
                }}
              />
            )}
          </FormRow>
        )}
      </Field>
    </FormSection>
  );
};

export default GlobalServiceEnvironment;
